import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { BRAND, IMAGES } from "../../lib/site";
import { MaskedLines } from "../../lib/motion";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden pt-28 md:pt-32">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left: type */}
          <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="font-body text-xs md:text-sm uppercase tracking-[0.3em] text-[#c96c31] mb-6"
              data-testid="hero-overline"
            >
              African Braiding Atelier — Landover, MD
            </motion.p>

            <h1 className="font-display font-light leading-[0.92] tracking-tight text-[#2c1e16] text-6xl sm:text-7xl lg:text-8xl">
              <MaskedLines lines={["The art of", "elevated"]} delay={0.35} />
              <MaskedLines
                lines={["braiding."]}
                delay={0.6}
                className="italic text-[#c96c31]"
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="mt-8 max-w-md font-body text-base md:text-lg text-[#2c1e16]/70 leading-relaxed"
              data-testid="hero-subtitle"
            >
              Complex braids, twists & cornrows crafted with obsessive precision.
              At {BRAND.name}, we complete for excellency — one crown at a time.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.8 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-book-btn"
                onClick={() => scrollTo("book")}
                className="rounded-full bg-[#c96c31] px-8 py-4 font-body text-sm font-medium uppercase tracking-wider text-[#f9f6f0] hover:bg-[#b35d27] hover:-translate-y-1 transition-all duration-300"
              >
                Book an Appointment
              </button>
              <a
                data-testid="hero-call-btn"
                href={BRAND.phoneHref}
                className="rounded-full border border-[#2c1e16]/25 px-8 py-4 font-body text-sm font-medium uppercase tracking-wider text-[#2c1e16] hover:border-[#c96c31] hover:text-[#c96c31] transition-all duration-300"
              >
                Call to Book
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6, duration: 1 }}
              className="mt-12 flex items-center gap-8"
            >
              <div>
                <p className="font-display text-4xl text-[#2c1e16]">7</p>
                <p className="font-body text-xs uppercase tracking-widest text-[#2c1e16]/50">Days a week</p>
              </div>
              <div className="h-10 w-px bg-[#2c1e16]/15" />
              <div>
                <p className="font-display text-4xl text-[#2c1e16]">10–30%</p>
                <p className="font-body text-xs uppercase tracking-widest text-[#2c1e16]/50">Off all services</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: art-directed image */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="arch-frame relative mx-auto aspect-[3/4] w-full max-w-md border border-[#2c1e16]/10"
            >
              <motion.img
                style={{ y: imgY, scale: imgScale }}
                src={IMAGES.g1}
                alt="Copper rope twists by Braiding Elevation"
                className="h-full w-full object-cover"
                data-testid="hero-image"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.8 }}
              className="absolute -bottom-4 -left-2 md:left-4 bg-[#2c1e16] text-[#f9f6f0] px-6 py-4 rounded-2xl max-w-[220px]"
            >
              <p className="font-display italic text-lg leading-tight">{`\u201C${BRAND.slogan}\u201D`}</p>
              <p className="font-body text-xs uppercase tracking-widest text-[#c96c31] mt-1">{BRAND.handle}</p>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="hidden md:flex items-center gap-2 absolute bottom-8 left-1/2 -translate-x-1/2 text-[#2c1e16]/50"
      >
        <ArrowDown size={16} className="animate-bounce" />
        <span className="font-body text-xs uppercase tracking-[0.25em]">Scroll</span>
      </motion.div>
    </section>
  );
};
